//====================================================
// src/components/block/liveStreamManagement/PupitreConfigModal.tsx
// v1.1 — Modale unifiée "Config pupitre"
//   Onglet 0 : Affectation des sources  (ex-SourceAliasConfigModal)
//   Onglet 1 : Raccourcis clavier       (ex-KeyboardShortcutsModal)
//
// v1.1 (2026-10-23) — présentation revue pour la lisibilité, logique inchangée :
// textes de 11 à 13 px (9–10 avant), contrastes relevés ; affectation en lignes avec
// pastille numérotée et icône du type de source ; raccourcis dessinés en touches de
// clavier (une touche par modificateur), compteur par section.

import React, {
  useState, useCallback, useEffect, useLayoutEffect, useRef,
} from 'react';
import ReactDOM from 'react-dom';
import {
  Settings, LayoutGrid, Keyboard, Camera, Smartphone, Globe, Monitor, Film, Layers,
  RotateCcw, X, AlertTriangle,
} from 'lucide-react';
import Window from '../../widget/windows/window';

import {
  VideoSource, SLOT_COUNT, JingleSlot, LayoutSlot,
} from '../../../contexts/MediaStreamContext';
import {
  DeckKeyMap, KeyBinding, DEFAULT_KEY_MAP, ROW_CONFIG,
} from '../../../dataTypes/liveStreamManagment/types_deck';
import { formatKeyBinding, findConflict } from '../../../dataTypes/liveStreamManagment/utils_deck';
import { SourceAliasMap } from './SourceAliasConfigModal';

// ── Helpers ───────────────────────────────────────────────────────────────────

const EMOJI_RE = /^[\p{Emoji_Presentation}\p{Extended_Pictographic}\u200d\ufe0f\s]+/u;
const cleanLabel = (raw: string) => raw.replace(EMOJI_RE, '').trim();

type SlotValue = string;

function slotValueFromState(vs: VideoSource | null, layout: LayoutSlot | null): SlotValue {
  if (layout) return `scene:${layout.id}`;
  if (vs)     return vs.id;
  return '';
}

function cloneKeyMap(km: DeckKeyMap): DeckKeyMap {
  return {
    video:   [...km.video],
    preview: [...km.preview],
    cut:     [...km.cut],
    image:   [...km.image],
    sound:   [...km.sound],
    pip:     [...km.pip],
    jingle:  [...km.jingle],
    layout:  [...km.layout],
  };
}

// ── Palette (v1.1) ────────────────────────────────────────────────────────────

const C = {
  text:    '#f1f5f9',
  soft:    '#cbd5e1',
  muted:   '#94a3b8',
  faint:   '#64748b',
  line:    'rgba(255,255,255,0.08)',
  accent:  '#818cf8',
  accentBg:'rgba(99,102,241,0.16)',
};

/** Libellé d'une rangée sans son emoji de tête (« 🎬 Sources vidéo » → « Sources vidéo »). */
const rowTitle = (row: keyof DeckKeyMap) => ROW_CONFIG[row].label.replace(/^[^ ]+ /, '');

// ── KeyCaps : un raccourci dessiné en touches de clavier ──────────────────────

const keycapStyle = (tone: 'normal' | 'capture' | 'conflict' | 'empty', small = false): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  minWidth: small ? 20 : 26, height: small ? 20 : 26, padding: small ? '0 5px' : '0 7px',
  borderRadius: 6, boxSizing: 'border-box',
  fontSize: small ? 10 : 13, fontWeight: 700, lineHeight: 1, whiteSpace: 'nowrap',
  fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
  ...(tone === 'empty'
    ? { color: C.faint, border: '1px dashed rgba(255,255,255,0.18)', background: 'transparent' }
    : tone === 'conflict'
    ? { color: '#fecaca', background: 'linear-gradient(#7f1d1d, #450a0a)', border: '1px solid #f87171', borderBottomWidth: 3 }
    : tone === 'capture'
    ? { color: '#e0e7ff', background: 'linear-gradient(#4338ca, #312e81)', border: '1px solid #818cf8', borderBottomWidth: 3 }
    : { color: '#f8fafc', background: 'linear-gradient(#334155, #1e293b)', border: '1px solid rgba(255,255,255,0.16)',
        borderBottom: '3px solid rgba(0,0,0,0.5)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)' }),
});

const KeyCaps: React.FC<{ kb: KeyBinding | null; capturing?: boolean; conflict?: boolean }> = ({ kb, capturing, conflict }) => {
  if (capturing) return <span className="pup-kb-blink" style={keycapStyle('capture')}>…</span>;
  if (!kb)       return <span style={keycapStyle('empty')}>—</span>;
  const tone = conflict ? 'conflict' : 'normal';
  const mods = [kb.ctrl && 'Ctrl', kb.alt && 'Alt', kb.shift && 'Shift'].filter(Boolean) as string[];
  const key  = formatKeyBinding({ ...kb, ctrl: false, alt: false, shift: false });
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 3, maxWidth: '100%' }}>
      {mods.map(m => <span key={m} style={keycapStyle(tone, true)}>{m}</span>)}
      <span style={keycapStyle(tone)}>{key}</span>
    </span>
  );
};

/** Icône du type de ce qui occupe un emplacement du deck. */
const SlotTypeIcon: React.FC<{ kind: VideoSource['type'] | 'jingle' | 'scene' | null }> = ({ kind }) => {
  const Icon = kind === 'device' ? Camera : kind === 'remote' ? Smartphone : kind === 'url' ? Globe
             : kind === 'screen' ? Monitor : kind === 'jingle' ? Film : kind === 'scene' ? Layers : null;
  return Icon ? <Icon style={{ width: 15, height: 15, color: C.muted }} /> : null;
};

// ── SlotCell (onglet Raccourcis) ──────────────────────────────────────────────

interface SlotCellProps {
  row:         keyof DeckKeyMap;
  idx:         number;
  kb:          KeyBinding | null;
  slotLabel:   string | null;
  isCapturing: boolean;
  isConflict:  boolean;
  isHovered:   boolean;
  modified:    boolean;
  onCapture:   () => void;
  onHover:     (on: boolean) => void;
  onClear:     () => void;
  onReset:     () => void;
  /** Occupe toute la hauteur de son conteneur (cellule CUT alignée sur la rangée Preview). */
  fill?:       boolean;
}

const SlotCell: React.FC<SlotCellProps> = ({
  row, idx, kb, slotLabel,
  isCapturing, isConflict, isHovered, modified,
  onCapture, onHover, onClear, onReset, fill,
}) => {
  const { border, bg, text } = ROW_CONFIG[row];
  const isEmpty = !slotLabel;

  const borderColor = isConflict  ? '#f87171'
                    : isCapturing ? '#818cf8'
                    : isEmpty     ? 'rgba(255,255,255,0.05)'
                    : border;

  const bgColor     = isConflict  ? 'rgba(239,68,68,0.18)'
                    : isCapturing ? 'rgba(99,102,241,0.2)'
                    : isEmpty     ? 'rgba(255,255,255,0.02)'
                    : bg;

  return (
    <div
      className={isConflict ? 'pup-kb-shake' : ''}
      onMouseEnter={() => { if (!isEmpty) onHover(true); }}
      onMouseLeave={() => onHover(false)}
      onClick={() => { if (!isEmpty) onCapture(); }}
      title={isEmpty ? 'Emplacement vide : aucun raccourci à régler' : `Cliquer puis appuyer sur la touche voulue pour ${rowTitle(row)} #${idx + 1}`}
      style={{
        border: `1.5px solid ${borderColor}`,
        borderStyle: isEmpty ? 'dashed' : 'solid',
        borderRadius: 10,
        background: bgColor,
        padding: '8px 6px',
        cursor: isEmpty ? 'not-allowed' : 'pointer',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
        position: 'relative',
        transition: 'border-color 0.12s, background 0.12s, transform 0.12s',
        transform: isHovered && !isEmpty ? 'translateY(-1px)' : 'none',
        boxShadow: isConflict  ? '0 0 14px rgba(239,68,68,0.4)'
                 : isCapturing ? '0 0 14px rgba(129,140,248,0.45)'
                 : 'none',
        minHeight: 96,
        height: fill ? '100%' : undefined,
        boxSizing: 'border-box',
        userSelect: 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: isEmpty ? C.faint : text }}>
          {idx + 1}
        </span>
        {modified && !isEmpty && (
          <span
            style={{ width: 6, height: 6, borderRadius: '50%', background: '#fb923c', boxShadow: '0 0 4px #fb923c', display: 'inline-block' }}
            title="Valeur modifiée"
          />
        )}
      </div>

      <span style={{
        fontSize: row === 'cut' ? 13 : 11, fontWeight: row === 'cut' ? 800 : 500,
        color: isEmpty ? C.faint : isConflict ? '#fca5a5' : row === 'cut' ? text : C.soft,
        textAlign: 'center', lineHeight: 1.3,
        overflow: 'hidden', display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical' as React.CSSProperties['WebkitBoxOrient'],
        wordBreak: 'break-word', width: '100%',
      }}>
        {isEmpty ? 'Vide' : (slotLabel || '').replace(/^[📱🖥️📸🤳⧉🎬🖼🔊🌐🎞📐]\s*/, '')}
      </span>

      <div style={{ marginTop: 'auto', maxWidth: '100%' }}>
        {isEmpty ? <span style={{ ...keycapStyle('empty'), opacity: 0.5 }}>—</span>
                 : <KeyCaps kb={kb} capturing={isCapturing} conflict={isConflict} />}
      </div>

      {isHovered && !isCapturing && !isEmpty && (
        <div style={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 3 }}>
          {modified && (
            <button
              onClick={e => { e.stopPropagation(); onReset(); }}
              style={{ ...CELL_ACTION, background: 'rgba(251,146,60,0.22)', border: '1px solid rgba(251,146,60,0.55)', color: '#fdba74' }}
              title={`Restaurer la valeur par défaut (${formatKeyBinding((DEFAULT_KEY_MAP[row] as (KeyBinding | null)[])[idx])})`}
            ><RotateCcw style={{ width: 11, height: 11 }} /></button>
          )}
          {kb && (
            <button
              onClick={e => { e.stopPropagation(); onClear(); }}
              style={{ ...CELL_ACTION, background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.5)', color: '#fca5a5' }}
              title="Effacer ce raccourci"
            ><X style={{ width: 11, height: 11 }} /></button>
          )}
        </div>
      )}

      {isConflict && (
        <AlertTriangle style={{ position: 'absolute', top: 5, left: 5, width: 13, height: 13, color: '#f87171' }} aria-label="Conflit" />
      )}
    </div>
  );
};

/** Titre d'une section de raccourcis : pastille de couleur, nom, compteur à droite. */
const KbSectionTitle: React.FC<{ row: keyof DeckKeyMap; assigned: number; total: number; extra?: React.ReactNode }> = ({ row, assigned, total, extra }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
    <span style={{ width: 10, height: 10, borderRadius: 3, background: ROW_CONFIG[row].text, boxShadow: `0 0 8px ${ROW_CONFIG[row].border}` }} />
    <span style={{ fontSize: 13, fontWeight: 700, color: C.text, letterSpacing: '0.01em' }}>{rowTitle(row)}</span>
    {extra}
    <span style={{ flex: 1, height: 1, background: C.line }} />
    <span style={{ fontSize: 11, fontWeight: 600, color: C.muted, fontVariantNumeric: 'tabular-nums' }}>
      {assigned} / {total} raccourci{total > 1 ? 's' : ''}
    </span>
  </div>
);

const CELL_ACTION: React.CSSProperties = {
  width: 20, height: 20, borderRadius: 5, cursor: 'pointer', padding: 0,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

// ── Style en-têtes colonnes (onglet Sources) ──────────────────────────────────

const colHeaderStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 700,
  color: C.muted,
  textTransform: 'uppercase', letterSpacing: '0.06em',
};

// Champs de l'onglet Sources : même hauteur pour le sélecteur et l'alias.
const FIELD: React.CSSProperties = {
  height: 36, boxSizing: 'border-box', width: '100%', borderRadius: 8, outline: 'none',
  fontSize: 13, color: C.text, transition: 'border-color 0.15s, background 0.15s',
};

const SOURCES_GRID = '44px minmax(0,1.25fr) minmax(0,1fr) 32px';

const BTN_BASE: React.CSSProperties = {
  height: 34, padding: '0 18px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 6,
};
const BTN_GHOST:   React.CSSProperties = { ...BTN_BASE, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.14)', color: C.soft };
const BTN_PRIMARY: React.CSSProperties = { ...BTN_BASE, background: '#6366f1', border: '1px solid #818cf8', color: '#fff', boxShadow: '0 4px 14px rgba(99,102,241,0.35)' };
const BTN_DANGER:  React.CSSProperties = { ...BTN_BASE, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.4)', color: '#fca5a5' };

// ── Props ─────────────────────────────────────────────────────────────────────

export interface PupitreConfigModalProps {
  onClose: () => void;

  // ── Affectation des sources ──────────────────────────────────────────────
  availableSources:    VideoSource[];
  deckSlots:           (VideoSource | null)[];
  deckSlotLayouts:     (LayoutSlot | null)[];
  aliases:             SourceAliasMap;
  onAliasesChange:     (aliases: SourceAliasMap) => void;
  onSlotSourceChange?: (slotIndex: number, newSource: VideoSource | null) => void;
  onSlotLayoutChange?: (slotIndex: number, layout: LayoutSlot | null) => void;
  jingleSlots?:        JingleSlot[];
  layoutSlots?:        LayoutSlot[];
  activeJingleId?:     string | null;
  activeLayoutId?:     string | null;
  onTriggerJingle?:    (slot: JingleSlot) => void;
  onToggleLayout?:     (slot: LayoutSlot) => void;
  /** Remet toute la régie à zéro (bouton du pied de l'onglet Sources). */
  onResetRegie?:       () => void;

  // ── Raccourcis clavier ───────────────────────────────────────────────────
  keyMap:              DeckKeyMap;
  onKeyMapChange:      (km: DeckKeyMap) => void;
  videoSlotLabels:     (string | null)[];
  imageSlotLabels:     string[];
  soundSlotLabels:     string[];
  pipSlotLabels:       string[];
  jingleSlotLabels:    string[];
  layoutSlotLabels:    string[];
}

// ── Composant ─────────────────────────────────────────────────────────────────

const PupitreConfigModal: React.FC<PupitreConfigModalProps> = ({
  onClose,
  // sources
  availableSources, deckSlots, deckSlotLayouts, aliases, onAliasesChange,
  onSlotSourceChange, onSlotLayoutChange,
  jingleSlots = [], layoutSlots = [],
  activeJingleId = null, activeLayoutId = null,
  onTriggerJingle, onToggleLayout, onResetRegie,
  // keyboard
  keyMap, onKeyMapChange,
  videoSlotLabels, imageSlotLabels, soundSlotLabels, pipSlotLabels,
  jingleSlotLabels, layoutSlotLabels,
}) => {

  const [activeTab, setActiveTab] = useState<0 | 1>(0);
  const [showResetRegieConfirm, setShowResetRegieConfirm] = useState(false);

  // ══════════════════════════════════════════════════════════════════════════
  // ONGLET 0 — Affectation des sources
  // ══════════════════════════════════════════════════════════════════════════

  const [localAliases, setLocalAliases] = useState<SourceAliasMap>({ ...aliases });
  const [localSlotValues, setLocalSlotValues] = useState<SlotValue[]>(
    Array.from({ length: SLOT_COUNT }, (_, i) =>
      slotValueFromState(deckSlots[i] ?? null, deckSlotLayouts[i] ?? null)
    )
  );

  const handleSlotChange = useCallback((slotIndex: number, value: SlotValue) => {
    setLocalSlotValues(prev => {
      const next = [...prev];
      if (value && !value.startsWith('jingle:') && !value.startsWith('scene:')) {
        for (let i = 0; i < next.length; i++) {
          if (i !== slotIndex && next[i] === value) next[i] = '';
        }
      }
      next[slotIndex] = value;
      return next;
    });
  }, []);

  const handleAliasChange = useCallback((sourceId: string, value: string) => {
    setLocalAliases(prev => ({ ...prev, [sourceId]: value }));
  }, []);

  const handleResetAlias = useCallback((sourceId: string) => {
    setLocalAliases(prev => { const next = { ...prev }; delete next[sourceId]; return next; });
  }, []);

  const handleConfirm = useCallback(() => {
    onAliasesChange(localAliases);

    const initialValues = Array.from({ length: SLOT_COUNT }, (_, i) =>
      slotValueFromState(deckSlots[i] ?? null, deckSlotLayouts[i] ?? null)
    );

    for (let i = 0; i < SLOT_COUNT; i++) {
      const prev = initialValues[i];
      const next = localSlotValues[i] ?? '';
      if (prev === next) continue;

      if (next.startsWith('jingle:')) {
        const slot = jingleSlots.find(j => j.id === next.slice('jingle:'.length));
        if (slot && onTriggerJingle) onTriggerJingle(slot);
        continue;
      }
      if (next.startsWith('scene:')) {
        const layout = layoutSlots.find(l => l.id === next.slice('scene:'.length)) ?? null;
        onSlotLayoutChange?.(i, layout);
        onSlotSourceChange?.(i, null);
        continue;
      }
      const newSrc = next ? (availableSources.find(s => s.id === next) ?? null) : null;
      onSlotLayoutChange?.(i, null);
      onSlotSourceChange?.(i, newSrc);
    }

    onClose();
  }, [
    localAliases, localSlotValues, deckSlots, deckSlotLayouts,
    availableSources, jingleSlots, layoutSlots,
    onAliasesChange, onSlotSourceChange, onSlotLayoutChange,
    onTriggerJingle, onClose,
  ]);

  const videoSourceForValue = (value: SlotValue): VideoSource | null => {
    if (!value || value.startsWith('jingle:') || value.startsWith('scene:')) return null;
    return availableSources.find(s => s.id === value) ?? null;
  };

  const aliasPlaceholder = (value: SlotValue): string => {
    if (value.startsWith('scene:'))  return layoutSlots.find(l => l.id === value.slice('scene:'.length))?.label ?? '—';
    if (value.startsWith('jingle:')) return jingleSlots.find(j => j.id === value.slice('jingle:'.length))?.label ?? '—';
    const src = videoSourceForValue(value);
    return src ? (cleanLabel(src.label) || src.id) : '—';
  };

  const availableJingles = jingleSlots.filter(j => j.url);
  const availableScenes  = layoutSlots.filter(l => l.background.type !== 'none' || l.incrustations.length > 0);
  const slots = Array.from({ length: SLOT_COUNT }, (_, i) => ({ index: i, value: localSlotValues[i] ?? '' }));

  // ══════════════════════════════════════════════════════════════════════════
  // ONGLET 1 — Raccourcis clavier
  // ══════════════════════════════════════════════════════════════════════════

  const [capturing,    setCapturing]    = useState<{ row: keyof DeckKeyMap; idx: number } | null>(null);
  const [conflictSlot, setConflictSlot] = useState<{ row: keyof DeckKeyMap; idx: number } | null>(null);
  const [conflictMsg,  setConflictMsg]  = useState<string | null>(null);
  const [hoveredSlot,  setHoveredSlot]  = useState<{ row: keyof DeckKeyMap; idx: number } | null>(null);

  const previewGridRef = useRef<HTMLDivElement>(null);
  const cutCellRef     = useRef<HTMLDivElement>(null);

  const labelsMap: Record<keyof DeckKeyMap, (string | null)[]> = {
    video:   videoSlotLabels,
    preview: videoSlotLabels,
    cut:     ['CUT'],
    image:   imageSlotLabels,
    sound:   soundSlotLabels,
    pip:     pipSlotLabels,
    jingle:  jingleSlotLabels,
    layout:  layoutSlotLabels,
  };

  useLayoutEffect(() => {
    if (activeTab !== 1) return;
    const update = () => {
      if (!previewGridRef.current || !cutCellRef.current) return;
      const cells = previewGridRef.current.querySelectorAll(':scope > div');
      if (cells.length === 0) return;
      cutCellRef.current.style.width = `${(cells[0] as HTMLElement).getBoundingClientRect().width}px`;
    };
    update();
    const ro = new ResizeObserver(update);
    if (previewGridRef.current) ro.observe(previewGridRef.current);
    return () => ro.disconnect();
  }, [activeTab]);

  useEffect(() => {
    if (!conflictSlot) return;
    const t = setTimeout(() => { setConflictSlot(null); setConflictMsg(null); }, 2000);
    return () => clearTimeout(t);
  }, [conflictSlot]);

  useEffect(() => {
    if (!capturing) return;
    const handler = (e: KeyboardEvent) => {
      if (e.code === 'Escape') { e.preventDefault(); setCapturing(null); return; }
      if (['Control', 'Shift', 'Alt', 'Meta', 'CapsLock', 'Tab'].includes(e.key)) return;
      e.preventDefault(); e.stopPropagation();
      const nb: KeyBinding = {
        code:    e.code,
        display: e.key.length === 1 ? e.key : undefined,
        ctrl:    e.ctrlKey, shift: e.shiftKey, alt: e.altKey,
      };
      const c = findConflict(keyMap, nb, capturing.row, capturing.idx);
      if (c) {
        setConflictSlot(c);
        setConflictMsg(`⚠ Touche déjà utilisée par ${ROW_CONFIG[c.row].label.replace(/^[^ ]+ /, '')} #${c.idx + 1} — raccourci non assigné`);
        setCapturing(null);
        return;
      }
      const next = cloneKeyMap(keyMap);
      (next[capturing.row] as (KeyBinding | null)[])[capturing.idx] = nb;
      onKeyMapChange(next);
      setCapturing(null); setConflictMsg(null); setConflictSlot(null);
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [capturing, keyMap, onKeyMapChange]);

  const clearBinding = (row: keyof DeckKeyMap, idx: number) => {
    const next = cloneKeyMap(keyMap);
    (next[row] as (KeyBinding | null)[])[idx] = null;
    onKeyMapChange(next);
    setConflictMsg(null); setConflictSlot(null);
  };

  const resetOneBinding = (row: keyof DeckKeyMap, idx: number) => {
    const defaultVal = (DEFAULT_KEY_MAP[row] as (KeyBinding | null)[])[idx] ?? null;
    if (defaultVal) {
      const c = findConflict(keyMap, defaultVal, row, idx);
      if (c) {
        setConflictSlot(c);
        setConflictMsg(`⚠ La valeur par défaut (${formatKeyBinding(defaultVal)}) est déjà utilisée par ${ROW_CONFIG[c.row].label.replace(/^[^ ]+ /, '')} #${c.idx + 1} — non restaurée`);
        return;
      }
    }
    const next = cloneKeyMap(keyMap);
    (next[row] as (KeyBinding | null)[])[idx] = defaultVal;
    onKeyMapChange(next);
    setConflictMsg(null); setConflictSlot(null);
  };

  const isModified = (row: keyof DeckKeyMap, idx: number): boolean => {
    const cur = (keyMap[row] as (KeyBinding | null)[])[idx];
    const def = (DEFAULT_KEY_MAP[row] as (KeyBinding | null)[])[idx];
    if (!cur && !def) return false;
    if (!cur || !def) return true;
    return cur.code !== def.code || cur.ctrl !== def.ctrl || cur.shift !== def.shift || cur.alt !== def.alt;
  };

  const startCapture = (row: keyof DeckKeyMap, idx: number, isCapt: boolean) => {
    setConflictMsg(null); setConflictSlot(null);
    setCapturing(isCapt ? null : { row, idx });
  };

  const renderKbSection = (
    row: keyof DeckKeyMap,
    cols: number,
    labels: (string | null)[],
    bindings: (KeyBinding | null)[],
  ) => (
    <div key={row} style={{ marginBottom: 24 }}>
      <KbSectionTitle row={row} assigned={bindings.filter((b, i) => b && labels[i]).length} total={bindings.length} />
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 8 }}>
        {bindings.map((kb, idx) => (
          <SlotCell key={idx}
            row={row} idx={idx} kb={kb}
            slotLabel={labels[idx] ?? null}
            isCapturing={capturing?.row === row && capturing?.idx === idx}
            isConflict={conflictSlot?.row === row && conflictSlot?.idx === idx}
            isHovered={hoveredSlot?.row === row && hoveredSlot?.idx === idx}
            modified={isModified(row, idx)}
            onCapture={() => startCapture(row, idx, capturing?.row === row && capturing?.idx === idx)}
            onHover={on => setHoveredSlot(on ? { row, idx } : null)}
            onClear={() => clearBinding(row, idx)}
            onReset={() => resetOneBinding(row, idx)}
          />
        ))}
      </div>
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <style>{`
        @keyframes pup-shake {
          0%,100% { transform: translateX(0); }
          20%      { transform: translateX(-5px); }
          40%      { transform: translateX(5px); }
          60%      { transform: translateX(-4px); }
          80%      { transform: translateX(3px); }
        }
        .pup-kb-shake { animation: pup-shake 0.35s ease; }
        .pup-tab[aria-selected="false"]:hover { background: rgba(255,255,255,0.06) !important; color: #e2e8f0 !important; }
        .pup-row:hover { background: rgba(255,255,255,0.025) !important; }
        .pup-field:focus { border-color: #818cf8 !important; box-shadow: 0 0 0 3px rgba(99,102,241,0.25); }
        .pup-field::placeholder { color: #64748b; }
        @keyframes pup-blink { 0%,100% { opacity: 1; } 50% { opacity: 0.45; } }
        .pup-kb-blink { animation: pup-blink 1s ease-in-out infinite; }
        .sam-select option, .sam-select optgroup { background: #1a2035 !important; color: #e2e8f0 !important; }
        .sam-select optgroup { color: #94a3b8 !important; font-weight: 600; }
      `}</style>

      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--color-Panel-niv1-BG)',
          borderRadius: 16,
          width: '100%',
          maxWidth: activeTab === 1 ? 1140 : 780,
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 32px 80px rgba(0,0,0,0.85)',
          transition: 'max-width 0.2s ease',
        }}
      >
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div style={{
          padding: '14px 20px',
          borderBottom: `1px solid ${C.line}`,
          display: 'flex', alignItems: 'center', gap: 12,
          flexShrink: 0,
        }}>
          <span style={{ width: 32, height: 32, borderRadius: 9, background: C.accentBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Settings style={{ width: 16, height: 16, color: C.accent }} />
          </span>
          <span style={{ fontSize: 16, fontWeight: 700, color: C.text, flexShrink: 0 }}>Config pupitre</span>

          {/* Onglets — contrôle segmenté */}
          <div role="tablist" style={{ display: 'flex', gap: 2, marginLeft: 16, padding: 3, borderRadius: 10, background: 'rgba(0,0,0,0.28)', border: `1px solid ${C.line}` }}>
            {([['Affectation des sources', LayoutGrid], ['Raccourcis clavier', Keyboard]] as const).map(([label, Icon], i) => (
              <button
                key={i}
                role="tab"
                aria-selected={activeTab === i}
                className="pup-tab"
                onClick={() => setActiveTab(i as 0 | 1)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7,
                  background: activeTab === i ? '#6366f1' : 'transparent',
                  border: 'none', borderRadius: 7,
                  color: activeTab === i ? '#fff' : C.muted,
                  fontSize: 13, fontWeight: 600,
                  padding: '7px 14px',
                  cursor: 'pointer',
                  boxShadow: activeTab === i ? '0 2px 10px rgba(99,102,241,0.35)' : 'none',
                  transition: 'color 0.15s, background 0.15s',
                }}
              ><Icon style={{ width: 14, height: 14 }} />{label}</button>
            ))}
          </div>
          <span style={{ flex: 1 }} />

          <button
            onClick={onClose}
            title="Fermer"
            style={{
              background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.line}`,
              color: C.soft, width: 32, height: 32,
              borderRadius: 9, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          ><X style={{ width: 16, height: 16 }} /></button>
        </div>

        {/* ── Bannières raccourcis (onglet 1 seulement) ───────────────────── */}
        {activeTab === 1 && conflictMsg && (
          <div style={{ padding: '10px 20px', background: 'rgba(239,68,68,0.12)', borderBottom: '1px solid rgba(239,68,68,0.3)', fontSize: 13, color: '#fecaca', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <AlertTriangle style={{ width: 16, height: 16, color: '#f87171', flexShrink: 0 }} />
            {conflictMsg.replace(/^⚠\s*/, '')}
            <button onClick={() => { setConflictMsg(null); setConflictSlot(null); }} title="Masquer" style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#fca5a5', cursor: 'pointer', display: 'flex' }}><X style={{ width: 15, height: 15 }} /></button>
          </div>
        )}
        {activeTab === 1 && capturing && (
          <div style={{ padding: '10px 20px', background: 'rgba(99,102,241,0.16)', borderBottom: '1px solid rgba(99,102,241,0.35)', fontSize: 14, color: '#e0e7ff', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <Keyboard className="pup-kb-blink" style={{ width: 18, height: 18, color: C.accent }} />
            Appuyez sur une touche pour <strong>{rowTitle(capturing.row)} #{capturing.idx + 1}</strong>
            <span style={{ marginLeft: 'auto', fontSize: 12, color: '#c7d2fe', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={keycapStyle('normal', true)}>Échap</span> pour annuler
            </span>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            ONGLET 0 — Affectation des sources
            ════════════════════════════════════════════════════════════════════ */}
        {activeTab === 0 && (
          <>
            {/* Résumé + en-têtes colonnes */}
            <div style={{ padding: '12px 20px 0', flexShrink: 0 }}>
              <div style={{ fontSize: 13, color: C.soft, marginBottom: 10 }}>
                <strong style={{ color: C.text, fontVariantNumeric: 'tabular-nums' }}>{slots.filter(s => s.value).length}</strong> emplacement{slots.filter(s => s.value).length > 1 ? 's' : ''} du deck affecté{slots.filter(s => s.value).length > 1 ? 's' : ''} sur {SLOT_COUNT}
                <span style={{ color: C.faint }}> · l'alias remplace le nom de la source sur le deck</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: SOURCES_GRID, gap: 10, padding: '0 0 8px', borderBottom: `1px solid ${C.line}` }}>
                <span style={{ ...colHeaderStyle, textAlign: 'center' }}>N°</span>
                <span style={colHeaderStyle}>Source affectée</span>
                <span style={colHeaderStyle}>Alias affiché</span>
                <span />
              </div>
            </div>

            {/* Liste */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '4px 12px 8px' }}>
              {slots.map(({ index, value }) => {
                const videoSrc     = videoSourceForValue(value);
                const sourceId     = videoSrc?.id ?? '';
                const currentAlias = sourceId ? (localAliases[sourceId] ?? '') : '';
                const hasCustom    = !!currentAlias.trim();
                const hasValue     = !!value;
                const canAlias     = !!videoSrc;
                const kind         = value.startsWith('jingle:') ? 'jingle' : value.startsWith('scene:') ? 'scene' : videoSrc?.type ?? null;

                return (
                  <div key={index} className="pup-row" style={{ display: 'grid', gridTemplateColumns: SOURCES_GRID, alignItems: 'center', gap: 10, padding: '6px 8px', borderRadius: 10 }}>

                    <span style={{
                      width: 32, height: 32, justifySelf: 'center', borderRadius: 9, boxSizing: 'border-box',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                      ...(hasValue
                        ? { background: C.accentBg, border: '1px solid rgba(129,140,248,0.5)', color: '#e0e7ff' }
                        : { background: 'transparent', border: '1px dashed rgba(255,255,255,0.18)', color: C.faint }),
                    }}>
                      {index + 1}
                    </span>

                    <div style={{ position: 'relative' }}>
                      {kind && (
                        <span style={{ position: 'absolute', left: 11, top: 0, bottom: 0, display: 'flex', alignItems: 'center', pointerEvents: 'none' }}>
                          <SlotTypeIcon kind={kind} />
                        </span>
                      )}
                    <select
                      className="sam-select pup-field"
                      value={value}
                      onChange={e => handleSlotChange(index, e.target.value)}
                      style={{ ...FIELD, background: hasValue ? '#0b1222' : 'rgba(255,255,255,0.03)', border: `1px solid ${hasValue ? 'rgba(129,140,248,0.4)' : 'rgba(255,255,255,0.12)'}`, color: hasValue ? C.text : C.muted, padding: kind ? '0 10px 0 34px' : '0 10px', cursor: 'pointer', appearance: 'auto' as const, colorScheme: 'dark', WebkitTextFillColor: hasValue ? C.text : C.muted }}
                    >
                      <option value="">— Aucune —</option>
                      {availableSources.filter(s => s.type !== 'scene' && s.type !== 'screen').length > 0 && (
                        <optgroup label="Sources vidéo">
                          {availableSources.filter(s => s.type !== 'scene' && s.type !== 'screen').map(s => <option key={s.id} value={s.id}>{cleanLabel(s.label) || s.id}</option>)}
                        </optgroup>
                      )}
                      {availableSources.filter(s => s.type === 'screen' && s.screenActive).length > 0 && (
                        <optgroup label="Écran">
                          {availableSources.filter(s => s.type === 'screen' && s.screenActive).map(s => <option key={s.id} value={s.id}>{cleanLabel(s.label) || s.id}</option>)}
                        </optgroup>
                      )}
                      {availableJingles.length > 0 && (
                        <optgroup label="Jingles vidéo">
                          {availableJingles.map(j => <option key={`jingle:${j.id}`} value={`jingle:${j.id}`}>{cleanLabel(j.label) || j.id}{activeJingleId === j.id ? ' ▶' : ''}</option>)}
                        </optgroup>
                      )}
                      {availableScenes.length > 0 && (
                        <optgroup label="Scènes">
                          {availableScenes.map(l => <option key={`scene:${l.id}`} value={`scene:${l.id}`}>{cleanLabel(l.label) || l.id}{activeLayoutId === l.id ? ' ●' : ''}</option>)}
                        </optgroup>
                      )}
                    </select>
                    </div>

                    <input
                      type="text"
                      className="pup-field"
                      placeholder={canAlias ? aliasPlaceholder(value) : value ? 'Pas d’alias pour ce type' : '—'}
                      value={currentAlias}
                      disabled={!canAlias}
                      onChange={e => { if (sourceId) handleAliasChange(sourceId, e.target.value); }}
                      style={{ ...FIELD, padding: '0 12px', background: canAlias ? 'rgba(255,255,255,0.05)' : 'transparent', border: `1px ${canAlias ? 'solid' : 'dashed'} ${hasCustom ? 'rgba(129,140,248,0.55)' : 'rgba(255,255,255,0.12)'}`, fontWeight: hasCustom ? 600 : 400, cursor: canAlias ? 'text' : 'default', opacity: canAlias ? 1 : 0.55 }}
                    />

                    <button
                      onClick={() => { if (sourceId) handleResetAlias(sourceId); }}
                      disabled={!hasCustom}
                      title={hasCustom ? 'Réinitialiser l’alias (reprendre le nom de la source)' : 'Aucun alias à réinitialiser'}
                      style={{ width: 32, height: 32, borderRadius: 8, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: hasCustom ? 'rgba(255,255,255,0.06)' : 'transparent', border: `1px solid ${hasCustom ? C.line : 'transparent'}`, cursor: hasCustom ? 'pointer' : 'default', color: hasCustom ? C.soft : 'rgba(148,163,184,0.25)', transition: 'color 0.15s' }}
                    ><RotateCcw style={{ width: 14, height: 14 }} /></button>
                  </div>
                );
              })}
            </div>

            {/* Footer sources — Annuler / Valider */}
            <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.line}`, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, flexShrink: 0 }}>
              {onResetRegie && (
                <button
                  onClick={() => setShowResetRegieConfirm(true)}
                  title="Remettre la régie à zéro, comme à la création de la chaîne"
                  style={{ ...BTN_DANGER, marginRight: 'auto' }}
                >
                  <RotateCcw style={{ width: 14, height: 14 }} /> Tout réinitialiser
                </button>
              )}
              <button onClick={onClose} style={BTN_GHOST}>Annuler</button>
              <button onClick={handleConfirm} style={BTN_PRIMARY}>Valider</button>
            </div>
          </>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            ONGLET 1 — Raccourcis clavier
            ════════════════════════════════════════════════════════════════════ */}
        {activeTab === 1 && (
          <>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px 20px' }}>

              {renderKbSection('video', 12, labelsMap.video, keyMap.video)}

              {/* Preview + CUT aligné dynamiquement */}
              <div style={{ marginBottom: 24 }}>
                <KbSectionTitle
                  row="preview"
                  assigned={keyMap.preview.filter((b, i) => b && labelsMap.preview[i]).length + (keyMap.cut[0] ? 1 : 0)}
                  total={keyMap.preview.length + 1}
                  extra={<span style={{ fontSize: 12, fontWeight: 700, color: ROW_CONFIG.cut.text }}>+ CUT</span>}
                />

                <div style={{ position: 'relative', paddingRight: 'calc(8.33% + 8px)' }}>
                  <div ref={previewGridRef} style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 8 }}>
                    {keyMap.preview.map((kb, idx) => (
                      <SlotCell key={idx}
                        row="preview" idx={idx} kb={kb}
                        slotLabel={labelsMap.preview[idx] ?? null}
                        isCapturing={capturing?.row === 'preview' && capturing?.idx === idx}
                        isConflict={conflictSlot?.row === 'preview' && conflictSlot?.idx === idx}
                        isHovered={hoveredSlot?.row === 'preview' && hoveredSlot?.idx === idx}
                        modified={isModified('preview', idx)}
                        onCapture={() => startCapture('preview', idx, capturing?.row === 'preview' && capturing?.idx === idx)}
                        onHover={on => setHoveredSlot(on ? { row: 'preview', idx } : null)}
                        onClear={() => clearBinding('preview', idx)}
                        onReset={() => resetOneBinding('preview', idx)}
                      />
                    ))}
                  </div>

                  {/* Cellule CUT — largeur synchronisée sur une cellule preview */}
                  <div ref={cutCellRef} style={{ position: 'absolute', bottom: 0, right: 0, height: '100%' }}>
                    <SlotCell
                      fill
                      row="cut" idx={0} kb={keyMap.cut[0] ?? null}
                      slotLabel="CUT"
                      isCapturing={capturing?.row === 'cut' && capturing?.idx === 0}
                      isConflict={conflictSlot?.row === 'cut' && conflictSlot?.idx === 0}
                      isHovered={hoveredSlot?.row === 'cut' && hoveredSlot?.idx === 0}
                      modified={isModified('cut', 0)}
                      onCapture={() => startCapture('cut', 0, capturing?.row === 'cut' && capturing?.idx === 0)}
                      onHover={on => setHoveredSlot(on ? { row: 'cut', idx: 0 } : null)}
                      onClear={() => clearBinding('cut', 0)}
                      onReset={() => resetOneBinding('cut', 0)}
                    />
                  </div>
                </div>
              </div>

              {renderKbSection('image',  12, labelsMap.image,  keyMap.image)}
              {renderKbSection('sound',  12, labelsMap.sound,  keyMap.sound)}
              {renderKbSection('jingle', 12, labelsMap.jingle, keyMap.jingle)}
              {renderKbSection('layout', 12, labelsMap.layout, keyMap.layout)}
              {/* PiP : plus de bouton dans le deck, section masquée */}

              {/* Légende */}
              <div style={{ marginTop: 6, padding: '12px 16px', background: 'rgba(255,255,255,0.03)', borderRadius: 10, border: `1px solid ${C.line}`, fontSize: 12, color: C.muted, lineHeight: 1.7, display: 'grid', gap: 6 }}>
                <strong style={{ color: C.soft, fontSize: 13 }}>Comment ça marche</strong>
                <span>Cliquez une case, puis appuyez sur la touche voulue. Les touches portent ce qui est gravé sur <em>votre</em> clavier (AZERTY, QWERTY…).</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  Pavé numérique : préfixe ⌨, par exemple <span style={keycapStyle('normal', true)}>⌨1</span> au lieu de <span style={keycapStyle('normal', true)}>1</span>.
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#fb923c', display: 'inline-block' }} /> valeur modifiée par rapport au défaut ; au survol,
                  <RotateCcw style={{ width: 13, height: 13, color: '#fdba74' }} /> restaure et <X style={{ width: 13, height: 13, color: '#fca5a5' }} /> efface.
                </span>
              </div>
            </div>

            {/* Footer raccourcis — Tout réinitialiser / Fermer */}
            <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.line}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <button
                onClick={() => { onKeyMapChange(DEFAULT_KEY_MAP); setConflictMsg(null); setConflictSlot(null); setCapturing(null); }}
                style={BTN_GHOST}
                title="Réinitialiser tous les raccourcis aux valeurs par défaut"
              ><RotateCcw style={{ width: 14, height: 14 }} /> Tout réinitialiser</button>
              <button onClick={onClose} style={BTN_PRIMARY}>Fermer</button>
            </div>
          </>
        )}

      </div>
      {showResetRegieConfirm && onResetRegie && (
        <Window
          message={<>Tout réinitialiser ? La régie revient à son état de création : mediaboard vide, deck et multiview à leur disposition d'origine, réglages des sources par défaut, tous les effets coupés, et PLUS AUCUNE source externe enregistrée : flux HLS, comptes Xtream, caméras RTMP et smartphones sont supprimés. Pensez à sauvegarder le deck avant. À éviter pendant un direct.</>}
          messageType="question"
          onClickOnValidateButton={() => { setShowResetRegieConfirm(false); onClose(); onResetRegie(); }}
          onClickOnCancelButton={() => setShowResetRegieConfirm(false)}
          validButtonText="Oui"
          cancelButtonText="Non"
          zIndex={10001}
        />
      )}
    </div>,
    document.body
  );
};

export default PupitreConfigModal;
